import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { api, resolveMediaUrl } from '../../utils/api';
import ProtectedRoute from '../../components/ProtectedRoute';
import AdminLayout, { useTheme } from '../../components/AdminLayout';
import { useNotification } from '../../contexts/NotificationContext';
import Skeleton from '../../components/Skeleton';

function ArtistsContent() {
  const { t } = useTranslation();
  const { showAlert, showConfirm } = useNotification();
  const { T, S } = useTheme();
  const [shops, setShops] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedShopId, setSelectedShopId] = useState('');
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [artists, setArtists] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '',
    name_ar: '',
    role: '',
    photo_url: null,
    worker_tier: 'both',
    branch_id: '',
  });
  const [servicesModal, setServicesModal] = useState(null);
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);

  const fetchShops = () => {
    api.getAdminShops().then(setShops).catch(() => setShops([]));
  };

  useEffect(() => {
    fetchShops();
  }, []);

  useEffect(() => {
    if (!selectedShopId) {
      setBranches([]);
      setSelectedBranchId('');
      return;
    }
    api.getAdminBranches(parseInt(selectedShopId, 10))
      .then(setBranches)
      .catch(() => setBranches([]));
  }, [selectedShopId]);

  useEffect(() => {
    if (!selectedShopId) {
      setServices([]);
      return;
    }
    api.getAdminServices(parseInt(selectedShopId, 10))
      .then((data) => setServices(Array.isArray(data) ? data : []))
      .catch(() => setServices([]));
  }, [selectedShopId]);

  const fetchArtists = async () => {
    setLoading(true);
    try {
      const opts = {};
      if (selectedBranchId) opts.branchId = parseInt(selectedBranchId, 10);
      else if (selectedShopId) opts.shopId = parseInt(selectedShopId, 10);
      const data = await api.getAdminArtists(opts);
      setArtists(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setArtists([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedShopId && !selectedBranchId) {
      setArtists([]);
      setLoading(false);
      return;
    }
    fetchArtists();
  }, [selectedShopId, selectedBranchId]);

  const branchName = (bid) => branches.find((b) => b.id === bid)?.name || `Branch ${bid}`;

  const servicesForArtistBranch = useMemo(() => {
    if (!servicesModal) return [];
    const bid = servicesModal.branch_id;
    return services.filter((s) => s.branch_id === bid);
  }, [services, servicesModal]);

  const openCreate = () => {
    if (!selectedBranchId) {
      showAlert(t('admin.pleaseSelectBranch'));
      return;
    }
    setEditing('create');
    setForm({
      name: '',
      name_ar: '',
      role: '',
      photo_url: null,
      worker_tier: 'both',
      branch_id: parseInt(selectedBranchId, 10),
    });
  };

  const openEdit = (a) => {
    setEditing(a.id);
    setForm({
      name: a.name,
      name_ar: a.name_ar || '',
      role: a.role,
      photo_url: a.photo_url || null,
      worker_tier: a.worker_tier || 'both',
      branch_id: a.branch_id,
    });
  };

  const openServicesModal = async (artist) => {
    try {
      const res = await api.getAdminArtistServices(artist.id);
      setSelectedServiceIds(res.service_ids || []);
      setServicesModal(artist);
    } catch (err) {
      showAlert(err.message);
    }
  };

  const toggleService = (id) => setSelectedServiceIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const saveArtistServices = async () => {
    if (!servicesModal) return;
    try {
      await api.updateAdminArtistServices(servicesModal.id, { service_ids: selectedServiceIds });
      setServicesModal(null);
    } catch (err) {
      showAlert(err.message || t('common.error'));
    }
  };

  const uploadPhoto = async (e) => {
    if (!editing || editing === 'create' || !e.target?.files?.[0]) return;
    const file = e.target.files[0];
    if (!file.type.startsWith('image/')) {
      showAlert(t('manager.pleaseSelectImageFile'));
      return;
    }
    try {
      const fd = new FormData();
      fd.append('file', file);
      const updated = await api.uploadAdminArtistPhoto(editing, fd);
      setForm((f) => ({ ...f, photo_url: updated.photo_url }));
      fetchArtists();
    } catch (err) {
      showAlert(err.message || t('common.error'));
    }
    e.target.value = '';
  };

  const deletePhoto = () => {
    if (!editing || editing === 'create' || !form.photo_url) return;
    showConfirm(t('admin.deletePhotoConfirm', 'Delete this photo?'), async () => {
      try {
        const updated = await api.deleteAdminArtistPhoto(editing);
        setForm((f) => ({ ...f, photo_url: updated.photo_url ?? null }));
        fetchArtists();
      } catch (err) {
        showAlert(err.message);
      }
    });
  };

  const save = async () => {
    const bid = form.branch_id ? parseInt(form.branch_id, 10) : null;
    if (!bid) {
      showAlert(t('admin.pleaseSelectBranch'));
      return;
    }
    try {
      const payload = {
        branch_id: bid,
        name: form.name,
        name_ar: form.name_ar || null,
        role: form.role,
        worker_tier: form.worker_tier || 'both',
      };
      if (editing === 'create') {
        await api.createAdminArtist(payload);
      } else {
        await api.updateAdminArtist(editing, payload);
      }
      setEditing(null);
      fetchArtists();
    } catch (err) {
      showAlert(err.message || t('common.error'));
    }
  };

  const remove = async (id) => {
    showConfirm(t('manager.deleteWorkerConfirm'), async () => {
      try {
        await api.deleteAdminArtist(id);
        fetchArtists();
      } catch (err) {
        showAlert(err.message || t('common.error'));
      }
    });
  };

  return (
    <AdminLayout role="admin">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '1rem', marginBottom: '2rem' }}>
        <div>
          <h1 style={S.pageTitle}>{t('admin.workers')}</h1>
          <div style={{ width: '40px', height: '2px', background: T.orange, marginTop: '0.5rem' }} />
          <p style={{ fontSize: '0.82rem', color: T.muted, marginTop: '0.75rem' }}>
            {t('admin.selectShopAndBranch', 'Choose a salon and optionally a branch. Pick a branch to add workers.')}
          </p>
        </div>
        {selectedShopId && selectedBranchId && (
          <button type="button" style={S.btnOrange} onClick={openCreate}>
            {t('manager.addWorker')}
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.78rem', color: T.muted, marginBottom: '0.35rem' }}>{t('admin.selectSalon')}</label>
          <select
            value={selectedShopId}
            onChange={(e) => {
              setSelectedShopId(e.target.value);
              setSelectedBranchId('');
            }}
            style={{ ...S.select, width: 'auto', marginBottom: 0, minWidth: 180 }}
          >
            <option value="">{t('admin.chooseSalon')}</option>
            {shops.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.78rem', color: T.muted, marginBottom: '0.35rem' }}>{t('admin.selectBranch')}</label>
          <select
            value={selectedBranchId}
            onChange={(e) => setSelectedBranchId(e.target.value)}
            style={{ ...S.select, width: 'auto', marginBottom: 0, minWidth: 180 }}
            disabled={!selectedShopId}
          >
            <option value="">{t('admin.allBranches', 'All branches in salon')}</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>
      </div>

      {editing && (
        <div style={S.modal}>
          <div style={S.modalBox}>
            <h2 style={S.modalTitle}>{editing === 'create' ? t('manager.newWorker') : t('manager.editWorker')}</h2>
            {editing !== 'create' && (
              <div style={{ marginBottom: '1rem' }}>
                <p style={{ fontSize: '0.78rem', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>{t('manager.photo')}</p>
                {form.photo_url ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                    <img src={resolveMediaUrl(form.photo_url)} alt="" loading="lazy" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, border: `1px solid ${T.border || 'var(--border)'}` }} />
                    <label style={{ cursor: 'pointer', fontSize: '0.85rem', color: T.orange, textDecoration: 'underline' }}>
                      {t('manager.changePhoto')}
                      <input type="file" accept="image/*" onChange={uploadPhoto} style={{ display: 'none' }} />
                    </label>
                    <button type="button" onClick={deletePhoto} style={{ ...S.btnDanger, padding: '0.35rem 0.6rem', fontSize: '0.75rem' }}>
                      {t('common.delete')}
                    </button>
                  </div>
                ) : (
                  <label style={{ display: 'inline-block', fontSize: '0.85rem', color: T.orange, cursor: 'pointer', textDecoration: 'underline' }}>
                    {t('manager.uploadPhoto')}
                    <input type="file" accept="image/*" onChange={uploadPhoto} style={{ display: 'none' }} />
                  </label>
                )}
              </div>
            )}
            {editing !== 'create' && branches.length > 0 && (
              <>
                <label style={{ display: 'block', fontSize: '0.78rem', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>{t('admin.selectBranch')}</label>
                <select
                  value={form.branch_id}
                  onChange={(e) => setForm({ ...form, branch_id: parseInt(e.target.value, 10) })}
                  style={{ ...S.select, width: '100%', marginBottom: '0.85rem' }}
                >
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </>
            )}
            <input placeholder={t('manager.placeholderName')} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={S.input} />
            <input placeholder={t('manager.placeholderNameAr')} value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} style={S.input} dir="rtl" />
            <input placeholder={t('admin.role')} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} style={S.input} />
            <label style={{ display: 'block', fontSize: '0.78rem', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>{t('manager.workerTier')}</label>
            <select
              value={form.worker_tier}
              onChange={(e) => setForm({ ...form, worker_tier: e.target.value })}
              style={{ ...S.select, width: '100%', marginBottom: '1rem' }}
            >
              <option value="both">{t('manager.workerTierBoth')}</option>
              <option value="basic">{t('manager.workerTierBasic')}</option>
              <option value="vip">{t('manager.workerTierVip')}</option>
            </select>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button type="button" onClick={save} style={{ ...S.btnOrange, flex: 1 }}>{t('common.save')}</button>
              <button type="button" onClick={() => setEditing(null)} style={S.btnOutline}>{t('common.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {servicesModal && (
        <div style={S.modal}>
          <div style={{ ...S.modalBox, maxHeight: '80vh', overflowY: 'auto' }}>
            <h2 style={S.modalTitle}>{t('manager.servicesForName', { name: servicesModal.name })}</h2>
            <p style={{ fontSize: '0.82rem', color: T.muted, marginBottom: '1rem' }}>{t('manager.selectServicesWorkerCanPerform')}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.25rem' }}>
              {servicesForArtistBranch.length === 0 ? (
                <p style={{ color: T.muted }}>{t('manager.noServicesAddFirst')}</p>
              ) : (
                servicesForArtistBranch.map((svc) => (
                  <label key={svc.id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer', color: T.white, fontSize: '0.88rem' }}>
                    <input
                      type="checkbox"
                      checked={selectedServiceIds.includes(svc.id)}
                      onChange={() => toggleService(svc.id)}
                      style={{ accentColor: T.orange }}
                    />
                    {svc.name}
                    {' '}
                    (
                    {svc.duration_minutes}
                    {' '}
                    min,
                    {' '}
                    {svc.price}
                    {' '}
                    EGP)
                  </label>
                ))
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button type="button" onClick={saveArtistServices} style={{ ...S.btnOrange, flex: 1 }}>{t('common.save')}</button>
              <button type="button" onClick={() => setServicesModal(null)} style={S.btnOutline}>{t('common.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {!selectedShopId ? (
        <p style={{ color: T.muted }}>{t('admin.selectSalonFirst')}</p>
      ) : loading ? (
        <Skeleton count={5} height="48px" />
      ) : artists.length === 0 ? (
        <p style={{ color: T.muted }}>{t('manager.noWorkers')}</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr>
                {[t('manager.tablePhoto'), t('manager.tableId'), t('manager.tableName'), t('admin.branch'), t('manager.tableRole'), t('manager.tableWorkerTier'), t('manager.tableRating'), t('manager.tableActions')].map((h) => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {artists.map((a) => (
                <tr key={a.id}>
                  <td style={S.td}>
                    {a.photo_url ? (
                      <img src={resolveMediaUrl(a.photo_url)} alt="" loading="lazy" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6, border: `1px solid ${T.border || 'var(--border)'}` }} />
                    ) : (
                      <span style={{ color: T.muted, fontSize: '0.75rem' }}>—</span>
                    )}
                  </td>
                  <td style={S.td}>{a.id}</td>
                  <td style={S.td}>{a.name}</td>
                  <td style={S.td}>{branchName(a.branch_id)}</td>
                  <td style={S.td}>{a.role}</td>
                  <td style={S.td}>
                    {a.worker_tier === 'vip' ? t('manager.workerTierVip') : a.worker_tier === 'basic' ? t('manager.workerTierBasic') : t('manager.workerTierBoth')}
                  </td>
                  <td style={{ ...S.td, color: T.orange }}>
                    ★
                    {a.rating}
                  </td>
                  <td style={S.td}>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <button type="button" style={S.btnSmall} onClick={() => openEdit(a)}>{t('manager.edit')}</button>
                      <button type="button" style={S.btnSmall} onClick={() => openServicesModal(a)}>{t('manager.services')}</button>
                      <button type="button" style={S.btnDanger} onClick={() => remove(a.id)}>{t('manager.delete')}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}

export default function AdminArtists() {
  return (
    <ProtectedRoute requireSuperAdmin>
      <ArtistsContent />
    </ProtectedRoute>
  );
}
